import React, {
	Fragment,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTableInfiniteScroll } from "@agilant/toga-blox/dist/hooks/useTableInfiniteScroll";
import {
	DataTableContextProvider,
	useDataTableContext,
} from "../../hooks/useDataTableContext";
import PrimaryTableHeader from "./header/PrimaryTableHeader";
import { TableContentProps } from "../../types";
import { usePrivilegeCheck } from "../../../../../hooks";
import SingleLineShimmer from "../../../../layout/Shimmer";
import { PageDataProvider } from "../../../../../hooks/usePageDataContext";
import { PrimaryTable } from "..";
import usePageListDetails from "../../../../../hooks/usePageListDetails";
import { useLocation, useNavigate } from "react-router-dom";
import CopyToClipboardIconWrapper from "../../../CopyToClipboardIconWrapper";
import BaseToolTip from "../../../BaseToolTip";
import MockTableRow from "./MockTableRow";
import CustomCellExpansionComponent from "./tableActions/CustomCellExpansionComponent";

// TYPES
import { AdditionalData, TableDisplayData } from "../../types";
export interface ToggleProps {
	expanded: boolean;
	onExpandCell: () => void;
	truncated: string;
	fullText: string;
	className?: string;
	linkTextClassNames?: string;
	cellClickAction: () => void;
	showSeeUnitsButton?: boolean;
}
const ROW_HEIGHT = 48;

const PrimaryTableContent: React.FC<TableContentProps> = ({
	headerGroups,
	data,
	page,
	getTableProps,
	getTableBodyProps,
	prepareRow,
	activeIndex,
	handleClear,
	setSearchColumn,
	showDetailsBox,
	setResetSearch,
	editingHeader,
	searchText,
	setSearchText,
	setSubmittedSearchText,
	setSortColumn,
	setEditingHeader,
	setHeaderValue,
	columnMappings,
	slug,
	filterApplied,
	onHeaderSortClick,
	loadNextPage,
	callFromFilter = false,
	cellClickAction: onCellClick,
	selectedPurchaseOrderItemUuid,
	isTableSearchAndSortEnabled,
	hasTableRowExpansion,
	tableDisplayData,
}) => {
	//// HOOK INVOCATIONS ////
	const location = useLocation();
	const { isSupervisor, isAdmin } = usePrivilegeCheck();

	//// STATES ////
	const [lastCopiedText, setLastCopiedText] = useState<string>("");
	const [isNestedRowOpen, setIsNestedRowOpen] = useState<string>("");
	const [additionalData, setAdditionalData] = useState<AdditionalData>(
		{} as AdditionalData,
	);

	// check which page is being rendered - need for subtable logic

	const isPathnameInventory_Items = location.pathname === "/inventory_items";

	useEffect(() => {
		// nothing here clears lastCopiedText
		if (!lastCopiedText) return;
		const t = window.setTimeout(() => setLastCopiedText(""), 2000);
		return () => window.clearTimeout(t);
	}, [lastCopiedText]);

	const navigate = useNavigate();
	const { pageMeta } = usePageListDetails("inventory");
	const scrollRef = useRef<HTMLDivElement>(null);
	const [parentWidth, setParentWidth] = useState<number>(0);
	const {
		searchColumn,
		hasInfiniteScroll = false,
		infiniteScrollIsLoadingMore = false,
		handleRowClick,
		totalRecordsFromTable = 0,
		expandedCells,
		toggleCell,
		activeRowUuid,
		toggleRow,
		isNestedTable,
		isNestedChildTable,
		columnHeaders,
		setHasCustomCellToggle,
		displayStickyColumn,
	} = useDataTableContext();

	const deepUuid = new URLSearchParams(location.search).get(
		`${slug}:items-for-purchase-orders`,
	);

	const rowVirtualizer = useVirtualizer({
		count: hasInfiniteScroll ? page.length : 0,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 5,
	});
	const lastUrl = sessionStorage.getItem("inventoryLastUrl");
	const [shouldDeepLink, setShouldDeepLink] = useState(
		() => !!deepUuid && !!lastUrl,
	);

	useEffect(() => {
		if (shouldDeepLink && page.length > 0) {
			toggleRow(deepUuid!);
			setShouldDeepLink(false);
		}
	}, [shouldDeepLink, page.length, toggleRow, deepUuid]);

	//Get the secondary tableView slug from localStorage selectedGrouping
	const selectedGrouping = localStorage.getItem("selectedGrouping");

	let secondaryTableViewSlug;

	if (selectedGrouping && !isPathnameInventory_Items) {
		secondaryTableViewSlug =
			JSON.parse(selectedGrouping).tableSlugs?.nestedTableSlug || null;
	} else {
		secondaryTableViewSlug =
			(selectedGrouping &&
				JSON.parse(selectedGrouping).tableSlugs?.subtableSlug) ||
			null;
	}

	const virtualItems = rowVirtualizer.getVirtualItems();
	const totalSize = rowVirtualizer.getTotalSize();
	useTableInfiniteScroll({
		hasInfiniteScroll,
		infiniteScrollIsLoadingMore,
		pageLength: page.length,
		totalRecords: totalRecordsFromTable,
		virtualItems,
		loadNextPage,
		threshold: 5,
	});

	//// CLASSNAMES
	const pillColor =
		data.length === 0 ? "bg-crimson-700" : "bg-supply-blue-400";

	//// CONDITIONAL STYLING VARS
	const paddingTop =
		hasInfiniteScroll && virtualItems.length ? virtualItems[0].start : 0;
	const paddingBottom =
		hasInfiniteScroll && virtualItems.length
			? totalSize -
				(virtualItems[virtualItems.length - 1].start +
					virtualItems[virtualItems.length - 1].size)
			: 0;

	//// CONDITIONS INTO VARS
	const hasApprovalFilter = !!localStorage.getItem("approvalFilter");
	const showApprovalsMessage = isAdmin ? hasApprovalFilter : isSupervisor;
	const message = `No results for ${searchColumn} "${
		localStorage.getItem("submittedSearchText") || ""
	}"`;
	const noApprovalsMessage = `All caught up! No orders pending approval.`;
	const colCount = headerGroups[0]?.headers.length ?? 0;
	const isUnitsTable = columnHeaders.includes("Serial #");

	const staticTableCellExpand =
		tableDisplayData?.hasCellExpansion &&
		tableDisplayData?.hasCellExpansion?.length > 0;

	const showOpenChildSubtableOnPageLoad =
		isPathnameInventory_Items && !isUnitsTable;

	const heightClasses = isNestedChildTable
		? !isPathnameInventory_Items
			? "min-h-[300px] max-h-[300px]"
			: "min-h-[calc(100svh-350px)] max-h-[calc(100svh-350px)]"
		: "h-full max-h-full rounded-lg";

	return (
		<div
			ref={scrollRef}
			className={`relative overflow-auto ${heightClasses}`}
		>
			<table
				className="table-auto w-full min-w-max sortable "
				{...getTableProps()}
			>
				<PrimaryTableHeader
					setClearSearch={handleClear}
					setSearchColumn={setSearchColumn}
					showSlideMenu={showDetailsBox}
					setResetSearch={setResetSearch}
					headerGroups={headerGroups}
					editingHeader={editingHeader}
					searchText={searchText}
					setSearchText={setSearchText}
					setSubmittedSearchText={setSubmittedSearchText}
					setSortColumn={setSortColumn}
					setEditingHeader={setEditingHeader}
					setHeaderValue={setHeaderValue}
					columnMappings={columnMappings}
					slug={slug}
					searchResultsPillColor={pillColor}
					onHeaderSortClick={onHeaderSortClick}
					isTableSearchAndSortEnabled={isTableSearchAndSortEnabled}
				/>

				<tbody {...getTableBodyProps()}>
					{data.length === 0 ? (
						<tr>
							<td colSpan={headerGroups[0].headers.length}>
								<div
									className={`min-h-[550px] flex justify-start items-start text-xl p-10 ${
										isNestedChildTable
											? "justify-start items-start"
											: "justify-center items-center"
									}`}
								>
									{isNestedChildTable
										? "No Items Found"
										: showApprovalsMessage
											? noApprovalsMessage
											: message}
								</div>
							</td>
						</tr>
					) : showOpenChildSubtableOnPageLoad ? (
						// this condition is used to render the tables on the inventory subtable page
						// for now, it is hardcoded to render 'Items by Customer POs' view
						page.map((row, idx) => {
							prepareRow(row);
							const rowUuid = data[idx]?.data?.uuid!;

							return (
								<Fragment key={`${row.id}-${idx}`}>
									<MockTableRow
										additionalCellClassNames="bg-navy-50 w-full"
										cellColor={
											staticTableCellExpand
												? "bg-navy-50"
												: ""
										}
										cellMaxWidthWhenExpanded={30}
										displayStickyColumn={
											displayStickyColumn
										}
										key={`${row.id}-${idx}`}
										rowUuid={data[idx]?.data?.uuid}
										row={row}
										prepareRow={prepareRow}
										activeIndex={activeIndex}
										cellExpandable={true}
										hasInfiniteScroll={false}
										onRowClick={() => {}}
										rowHoverClasses={""}
										expandedCells={expandedCells}
										onExpandCell={toggleCell}
										maxCharacters={40}
										hasDropDown
										expandedRowUuid={
											selectedPurchaseOrderItemUuid
										}
										toggleComponent={(props) => (
											<CustomCellExpansionComponent
												{...props}
												cellClickAction={() =>
													onCellClick?.(
														undefined as any,
														rowUuid,
													)
												}
												setHasCustomCellToggle={
													setHasCustomCellToggle
												}
												showSeeUnitsButton={false}
												backgroundColor=""
											/>
										)}
										cellTextClassNames="p-5"
										linkTextClassNames="text-primary underline text-left pl-5 pb-5 "
										expandedContent={
											<div
												className="relative z-10 min-w-full  overflow-x-auto"
												style={{
													maxWidth: parentWidth,
												}}
											>
												<PageDataProvider
													pageMeta={pageMeta}
													pageTitle={"Inventory"}
												>
													<DataTableContextProvider
														isNestedChildTable={
															true
														}
														showStickyColumn={false}
														hasInfiniteScroll={true}
														hasModalTable={false}
														rowsHaveModalOnClick={
															true
														}
														data={[]}
													>
														<PrimaryTable
															callFromFilter={
																callFromFilter
															}
															pageTitle={""}
															hasModalTable={
																false
															}
															setPageTitle={() => {}}
															callClickAction={
																onCellClick
															}
															slug={
																"units-for-items-for-purchase-orders"
															}
															tableDisplayData={
																tableDisplayData.subTableChild as TableDisplayData
															}
															skipNavigateToPreviousUrl={
																true
															}
															subtableSlug={
																"SalesOrderItems"
															}
															subtableUuid={
																selectedPurchaseOrderItemUuid
															}
															isTableSearchAndSortEnabled={
																false
															}
															additionalData={
																additionalData
															}
														/>
													</DataTableContextProvider>
												</PageDataProvider>
											</div>
										}
										expandedCellActionButton={undefined}
									/>
								</Fragment>
							);
						})
					) : tableDisplayData?.hasInfiniteScroll ? (
						<div>
							<tr style={{ height: paddingTop }}>
								<td colSpan={headerGroups[0].headers.length} />
							</tr>

							{virtualItems.map(({ index }) => {
								const row = page[index];
								const rowUuid = data[index]?.data?.uuid!;

								const isNonSerialized =
									data?.[index]?.data?.inventorytype ===
									"NON_SERIALIZED";

								prepareRow(row);
								return (
									<MockTableRow
										cellColor="bg-white"
										cellMaxWidthWhenExpanded={30}
										key={`${row.id}-${index}`}
										rowUuid={data[index]?.data?.uuid}
										row={row}
										prepareRow={prepareRow}
										activeIndex={activeIndex}
										hasInfiniteScroll={
											tableDisplayData.hasInfiniteScroll ||
											false
										}
										onRowClick={
											tableDisplayData.isNestedTable
												? () => {}
												: handleRowClick
										}
										expandedCells={expandedCells}
										cellExpandable={true}
										onExpandCell={toggleCell}
										maxCharacters={25}
										additionalCellClassNames=""
										cellTextClassNames="p-5 pb-6 text-sm font-omnes bg-navy-50"
										linkTextClassNames="text-primary underline text-left pb-5 font-omnes text-sm bg-navy-50 pt-2"
										toggleComponent={(props) => (
											<CustomCellExpansionComponent
												{...props}
												cellClickAction={() =>
													onCellClick?.(
														undefined as any,
														rowUuid,
													)
												}
												showSeeUnitsButton={
													isNonSerialized
														? false
														: true
												}
												setHasCustomCellToggle={
													setHasCustomCellToggle
												}
											/>
										)}
										expandedCellActionButton={(
											fullText: string,
										) => (
											<div className="relative">
												<span
													className={`absolute -top-7 left-4
													w-6 h-6
													flex items-center justify-center
													border
													rounded-full
													${lastCopiedText ? "bg-green-100 border-green-600" : "border-primary"}
													`}
												>
													<BaseToolTip
														message={
															lastCopiedText ===
															fullText
																? "Copied"
																: "Copy"
														}
														toolTipPlacement="right"
													>
														<CopyToClipboardIconWrapper
															text={fullText}
															lastCopiedText={
																lastCopiedText
															}
															setLastCopiedText={
																setLastCopiedText
															}
															withText
															copyText=""
															hasIconBorder={
																false
															}
														/>
													</BaseToolTip>
												</span>
											</div>
										)}
										expandedContent={
											<div className="max-h-96 overflow-auto min-h-96 relative z-10">
												<PageDataProvider
													pageMeta={null}
													pageTitle={
														"Page title here"
													}
												>
													<DataTableContextProvider
														showStickyColumn
														hasInfiniteScroll={
															false
														}
														hasModalTable={false}
														data={[]}
														rowsHaveModalOnClick={
															false
														}
													>
														<PrimaryTable
															callFromFilter={
																callFromFilter
															}
															pageTitle={""}
															hasModalTable={
																false
															}
															setPageTitle={() => {}}
															tableDisplayData={
																tableDisplayData.subTableChild as TableDisplayData
															}
															slug={
																secondaryTableViewSlug
															}
															additionalData={
																additionalData
															}
														/>
													</DataTableContextProvider>
												</PageDataProvider>
											</div>
										}
									/>
								);
							})}

							<tr style={{ height: paddingBottom }}>
								<td colSpan={headerGroups[0].headers.length} />
							</tr>
						</div>
					) : (
						page.map((row, idx) => {
							prepareRow(row);
							const rowUuid = data[idx]?.data?.uuid!;

							return (
								<Fragment key={`${row.id}-${idx}`}>
									<MockTableRow
										additionalCellClassNames="bg-navy-50 w-full"
										cellColor={
											staticTableCellExpand
												? "bg-navy-50"
												: ""
										}
										cellMaxWidthWhenExpanded={30}
										key={`${row.id}-${idx}`}
										rowUuid={data[idx]?.data?.uuid}
										row={row}
										prepareRow={prepareRow}
										activeIndex={activeIndex}
										cellExpandable={staticTableCellExpand}
										hasInfiniteScroll={
											hasInfiniteScroll || false
										}
										onRowClick={
											isNestedTable
												? () => {}
												: handleRowClick
										}
										rowHoverClasses={`hover:bg-navy-100 ${
											isNestedTable
												? ""
												: "cursor-pointer"
										}`}
										expandedCellActionButton={(
											fullText: string,
										) => (
											<div className="relative">
												<span
													className={`absolute -top-5 left-4
													w-6 h-6
													flex items-center justify-center
													border
													rounded-full
													${lastCopiedText ? "bg-green-100 border-green-600" : "border-primary"}
													`}
												>
													<BaseToolTip
														message={
															lastCopiedText ===
															fullText
																? "Copied"
																: "Copy"
														}
														toolTipPlacement="right"
													>
														<CopyToClipboardIconWrapper
															text={fullText}
															lastCopiedText={
																lastCopiedText
															}
															setLastCopiedText={
																setLastCopiedText
															}
															withText
															copyText=""
															hasIconBorder={
																false
															}
														/>
													</BaseToolTip>
												</span>
											</div>
										)}
										expandedCells={expandedCells}
										onExpandCell={toggleCell}
										maxCharacters={40}
										hasDropDown
										expandedRowUuid={activeRowUuid}
										displayStickyColumn={
											displayStickyColumn
										}
										toggleComponent={(props) => (
											<CustomCellExpansionComponent
												{...props}
												cellClickAction={() =>
													onCellClick?.(
														undefined as any,
														rowUuid,
													)
												}
												showSeeUnitsButton={false}
												setHasCustomCellToggle={
													setHasCustomCellToggle
												}
											/>
										)}
										hasTableRowExpansion={
											hasTableRowExpansion
										}
										cellTextClassNames="p-5"
										linkTextClassNames="text-primary underline text-left pb-5"
										onToggleRow={async (uuid) => {
											toggleRow(uuid);
											setTimeout(() => {
												const params =
													new URLSearchParams(
														location.search,
													);

												const key = `${slug}:${secondaryTableViewSlug}`;
												const isNowOpen =
													uuid !== isNestedRowOpen;

												if (isNowOpen) {
													setIsNestedRowOpen(uuid);
													params.set(key, uuid);
												} else {
													setIsNestedRowOpen("");
													params.delete(key);
												}

												navigate(
													`${
														location.pathname
													}?${params.toString()}`,
													{
														replace: true,
													},
												);
											}, 0);
										}}
										expandedContent={
											tableDisplayData?.hasNestedTable &&
											!tableDisplayData?.isNestedTable ? (
												<div
													className="relative z-10 min-w-full  overflow-x-auto"
													style={{
														maxWidth: parentWidth,
													}}
												>
													<PageDataProvider
														pageMeta={pageMeta}
														pageTitle={"Inventory"}
													>
														<DataTableContextProvider
															isNestedChildTable={
																true
															}
															showStickyColumn={
																false
															}
															hasInfiniteScroll
															hasModalTable={
																false
															}
															rowsHaveModalOnClick={
																false
															}
															data={[]}
														>
															<PrimaryTable
																callFromFilter={
																	callFromFilter
																}
																pageTitle={""}
																hasModalTable={
																	false
																}
																setPageTitle={() => {}}
																callClickAction={
																	onCellClick
																}
																slug={
																	secondaryTableViewSlug
																}
																additionalData={
																	additionalData
																}
																skipNavigateToPreviousUrl={
																	true
																}
																isTableSearchAndSortEnabled={
																	false
																}
																tableDisplayData={
																	tableDisplayData.nestedTable as TableDisplayData
																}
															/>
														</DataTableContextProvider>
													</PageDataProvider>
												</div>
											) : (
												//needs to be a react fragment not NULL as NULL will show the UUID, this is Blox letting us know the UUID is found
												// but we are doing nothing with it
												<></>
											)
										}
									/>
								</Fragment>
							);
						})
					)}

					{infiniteScrollIsLoadingMore &&
						Array.from({ length: 10 }).map((_, r) => (
							<div className="min-w-full" key={r}>
								<div
									key={`shimmer-${r}`}
									className="border-b border-b-navy-200 w-full flex relative z-[-1]"
								>
									{Array.from({ length: colCount }).map(
										(__, c) => (
											<span
												key={c}
												className="w-full flex justify-start items-center mx-2"
											>
												<SingleLineShimmer
													randomWidth={true}
													minWidth={12}
													maxWidth={18}
													widthFraction={20}
													height="h-4"
													color="bg-gray-100"
												/>
											</span>
										),
									)}
								</div>
							</div>
						))}
				</tbody>
			</table>
		</div>
	);
};

export default PrimaryTableContent;
